import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import routesConstants from '../../constants/routes.constants';
import { PosthogService } from '../../@services/front/posthog.service';

/**
 * Carte « Crée ton compte » pour un invité (« Essayer sans compte »), posée là
 * où un bloc lui est fermé : la carte du jour de l'accueil (P1.0.8), le jour
 * lunaire (P1.3.3), Willy (P5.5). Même forme que le mur du quota de Willy :
 * Willy dans la carte verte, une phrase, un bouton « Créer mon compte ».
 *
 * Remplace le mode d'emploi en quatre étapes (« Déconnecte-toi, crée un
 * compte… ») : l'invité va droit à l'inscription, comme depuis l'encart de la
 * Lune (`lunar-detail.onTeaserClicked`).
 *
 * Usage : `<vw-guest-signup-card source="home" titleKey="…" messageKey="…" />`
 */
@Component({
  selector: 'vw-guest-signup-card',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <section class="flex flex-col gap-4 rounded-card bg-vw-surface-tint p-4" data-test="guest-signup-card">
      <div class="flex items-start gap-3">
        <img src="/assets/images/willy/willy_hello.png" alt=""
          class="h-16 w-16 shrink-0 rounded-pill bg-vw-surface object-contain object-bottom">
        <div class="min-w-0 grow">
          <h2 class="text-body font-semibold text-vw-primary-ink">{{ titleKey | translate }}</h2>
          @if (messageKey) {
            <p class="mt-1 text-body-s text-vw-primary-ink">{{ messageKey | translate }}</p>
          }
        </div>
      </div>
      <button type="button" data-test="guest-signup-cta" (click)="onSignup()"
        class="flex min-h-btn w-full items-center justify-center rounded-pill bg-vw-primary px-5 text-body font-semibold text-vw-on-color active:bg-vw-primary-pressed">
        {{ 'guest.signup.cta' | translate }}
      </button>
    </section>
  `,
})
export class GuestSignupCardComponent {
  private readonly router = inject(Router);
  private readonly posthog = inject(PosthogService);

  /** Ce que le compte ouvre ICI (clé i18n). */
  @Input({ required: true }) titleKey!: string;
  /** Précision sous le titre (clé i18n), facultative. */
  @Input() messageKey: string | null = null;
  /** Emplacement, pour savoir quelle carte amène des comptes. */
  @Input() source = 'unknown';
  /** La navigation part : l'hôte peut fermer une feuille (Willy) avant. */
  @Output() signup = new EventEmitter<void>();

  onSignup(): void {
    this.posthog.capture('guest_signup_clicked', { placement: this.source });
    this.signup.emit();
    void this.router.navigate([routesConstants.REGISTER]);
  }
}
